import { useCallback, useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SeoHead from '../components/SeoHead';
// TODO(@dvgup/branding migration): replace with `@dvgup/branding`'s
// runtime export once TODO-013 lands.
import { brandingFixture } from '../lib/branding/branding-fixture';
import { IdpButton } from '../components/auth/IdpButton';
import { useAppDispatch } from '../store/hooks';
import {
  authSignedIn,
  authSignInFailed,
  authSignInRequested,
} from '../store/slices/auth-slice';
import { getCognitoConfig } from '../lib/auth/cognito-config';
import { signInWithEmailPassword } from '../lib/auth/email-password';
import { storeTokens } from '../lib/auth/token-storage';

/**
 * Sign-in surface.
 *
 * Three federated buttons (Amazon, Google, Facebook) render disabled
 * with a "coming soon" tooltip until OAuth client credentials land
 * in Secrets Manager (TODO-005 / TODO-006 / TODO-014). The email +
 * password form below the divider is fully functional against the
 * Cognito public pool via the SDK's SRP auth flow.
 *
 * When IdP credentials land, flip the `disabled` prop on each
 * `<IdpButton>` to `false` and re-enable the `handleProviderClick`
 * flow (still exported below so the re-enable is a one-line change).
 */

export default function SignInPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userPoolId, userPoolClientId } = getCognitoConfig();
  const isConfigured = userPoolId !== '' && userPoolClientId !== '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const handleEmailSubmit = useCallback(
    async (evt: FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      setEmailError(undefined);
      setSubmitting(true);
      dispatch(authSignInRequested());
      try {
        const result = await signInWithEmailPassword({
          userPoolId,
          userPoolClientId,
          email,
          password,
        });
        storeTokens(userPoolClientId, {
          idToken: result.idToken,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          expiresAt: Date.now() + result.expiresIn * 1000,
        });
        dispatch(
          authSignedIn({
            idToken: result.idToken,
            accessToken: result.accessToken,
            cognitoSub: result.cognitoSub,
          }),
        );
        navigate('/');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'sign-in failed';
        setEmailError(msg);
        dispatch(authSignInFailed({ error: msg }));
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, email, navigate, password, userPoolClientId, userPoolId],
  );

  return (
    <Card sx={{ maxWidth: 480, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/sign-in"
          pageTitle="Sign in"
          branding={brandingFixture.share}
          jsonLdType="WebSite"
        />
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem', mb: 2 }}>
          Sign in
        </Typography>
        {!isConfigured && (
          <Alert severity="info" role="status" sx={{ mb: 2 }}>
            Sign-in temporarily unavailable
          </Alert>
        )}
        {isConfigured && (
          <Stack spacing={2}>
            <IdpButton provider="LoginWithAmazon" disabled />
            <IdpButton provider="Google" disabled />
            <IdpButton provider="Facebook" disabled />
            <Divider>or</Divider>
            <Box component="form" onSubmit={handleEmailSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                {emailError && <Alert severity="error">{emailError}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting || email.length === 0 || password.length === 0}
                >
                  {submitting ? 'Signing in…' : 'Sign in with email'}
                </Button>
              </Stack>
            </Box>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Need an account?{' '}
              <Link component={RouterLink} to="/sign-up">
                Sign up
              </Link>
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
